/*
 * AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2022 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */
import "./services/i18n";
import React from "react";
import ReactDOM from "react-dom/client";
import FlagProvider from "@unleash/proxy-client-react";
import {
  Navigate,
  RouterProvider,
  createBrowserRouter,
} from "react-router-dom";
import App from "./App";
import { Provider } from "react-redux";
import store from "./config/store";
import {
  AppLoader,
  initializeSentry,
  CaptchaProviderContext,
} from "@advapacs/common";
import setupAxiosInterceptors from "./config/axios-interceptor";
import { MatomoProvider } from "@datapunt/matomo-tracker-react";
import initializeMatomoTrackerInstance from "./matomoTracker";
import { MatomoInstance } from "@datapunt/matomo-tracker-react/lib/types";
import {
  APP_ENVIRONMENT,
  UNLEASH_CLIENT_KEY,
  UNLEASH_URL,
  UNLEASH_EDGE_CLIENT_KEY,
} from "./config/constants";

import axios from "axios";
import { getCustomerIdFromToken } from "./pages/unauthenticated/ducks/operations";
import { DashboardDefaultPage } from "./components/DashboardDefaultPage";
import { LinkNotFound } from "./components/LinkNotFound";
import { Navbar } from "./components/UnAuthenticationLayout/Navbar";
import { formatRegionUrl } from "@src/utils/utils";
import OauthCallback from "@src/pages/authenticated/account/OauthCallback";
import { TermsOfServicePage } from "./pages/authenticated/userLicense/TermsOfServicePage";
import { initializeFaro } from "./services/initializeFaro";

const root = ReactDOM.createRoot(
  document.getElementById("root") as HTMLElement
);

setupAxiosInterceptors(store);
initializeSentry();

const router = createBrowserRouter([
  {
    path: "/*",
    element: <App />,
  },
]);

const defaultRouter = createBrowserRouter([
  {
    path: "/",
    element: <DashboardDefaultPage />,
  },
  {
    path: "/OauthCallback",
    element: <OauthCallback />,
  },
  {
    path: "*",
    element: <Navigate to="/" />,
  },
  {
    path: "/public/terms-of-service",
    element: <TermsOfServicePage />,
  },
]);

/**
 * Default before load anything.
 */
root.render(
  <React.StrictMode>
    <AppLoader.Loader />
  </React.StrictMode>
);

/**
 * Load token from url for first time without router react.
 */
const link = new URL(window.location.href);
const pathParts = link.pathname?.split("/");

let token = "";
if (pathParts[1] === "t" && pathParts[2] != undefined) {
  token = pathParts[2];
}
// encoder the token to get region and access link id  for patient info.
getCustomerIdFromToken(token)
  .then((data) => {
    // Will set local storage to sync tab.
    localStorage.setItem("accessId", data?.accessLinkId ?? "");
    localStorage.setItem(`${window.location.host}.region`, data?.region ?? "");

    const finalUrl = formatRegionUrl(data?.region ?? "");
    initializeFaro(finalUrl);
    axios.defaults.baseURL = finalUrl;
    const url = finalUrl;

    if (data.newAccessLinkId) {
      localStorage.setItem("newAccessId", data.newAccessLinkId);
    }

    const unleashClientKey =
      APP_ENVIRONMENT === "development"
        ? UNLEASH_CLIENT_KEY
        : UNLEASH_EDGE_CLIENT_KEY;

    const config = {
      appName: "portal-ui",
      refreshInterval: 60,
      url: UNLEASH_URL || `${url}/featureFlags/api/frontend`,
      clientKey: unleashClientKey || "",
      environment: APP_ENVIRONMENT,
      storage: {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-shadow
        save: (name: string, data: any) => {
          localStorage.setItem(name, data);
        },
        get: (name: string) => localStorage.getItem(name),
      },
    };

    root.render(
      <React.StrictMode>
        <CaptchaProviderContext>
          <React.Suspense fallback={<AppLoader.Loader />}>
            <FlagProvider config={config}>
              <Provider store={store}>
                {/* @ts-ignore */}
                <MatomoProvider
                  value={
                    initializeMatomoTrackerInstance as unknown as MatomoInstance
                  }
                >
                  <RouterProvider router={router} />
                </MatomoProvider>
              </Provider>
            </FlagProvider>
          </React.Suspense>
        </CaptchaProviderContext>
      </React.StrictMode>
    );
  })
  .catch((err) => {
    // @ts-ignore
    if (err?.code === "1992") {
      root.render(
        <React.StrictMode>
          <React.Suspense fallback={<AppLoader.Loader />}>
            <Provider store={store}>
              {/* @ts-ignore */}
              <MatomoProvider
                value={
                  initializeMatomoTrackerInstance as unknown as MatomoInstance
                }
              >
                <Navbar />
                <LinkNotFound />
              </MatomoProvider>
            </Provider>
          </React.Suspense>
        </React.StrictMode>
      );
    } else {
      localStorage.setItem(`${window.location.host}.region`, "sgp");

      const finalUrl = formatRegionUrl("sgp");
      axios.defaults.baseURL = finalUrl;
      const url = finalUrl;
      initializeFaro(finalUrl);

      const unleashClientKey =
        APP_ENVIRONMENT === "development"
          ? UNLEASH_CLIENT_KEY
          : UNLEASH_EDGE_CLIENT_KEY;

      const config = {
        appName: "portal-ui",
        refreshInterval: 60,
        url: UNLEASH_URL || `${url}/featureFlags/api/frontend`,
        clientKey: unleashClientKey || "",
        environment: APP_ENVIRONMENT,
        storage: {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          save: (name: string, data: any) => {
            localStorage.setItem(name, data);
          },
          get: (name: string) => localStorage.getItem(name),
        },
      };

      root.render(
        <React.StrictMode>
          <React.Suspense fallback={<AppLoader.Loader />}>
            <FlagProvider config={config}>
              <Provider store={store}>
                {/* @ts-ignore */}
                <MatomoProvider
                  value={
                    initializeMatomoTrackerInstance as unknown as MatomoInstance
                  }
                >
                  <RouterProvider router={defaultRouter} />
                </MatomoProvider>
              </Provider>
            </FlagProvider>
          </React.Suspense>
        </React.StrictMode>
      );
    }
  });
